import React, { forwardRef } from 'react';

import { HStack, Icon, Text, VStack } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';

interface SidebarButtonProps extends React.PropsWithChildren {
  label: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

interface SidebarButtonContentProps extends React.PropsWithChildren {
  showMenuIcon?: boolean;
}

const hoverAndFocusStyles = {
  backgroundColor: 'primitives.white.100alpha10',
  borderColor: 'border.weak',
};

// NOTE: These styles are used to create a basic unstyled button for things that
// need button semantics but not the full Samba Button styles.
export const baseUnstyledButtonStyles = {
  all: 'unset',
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  cursor: 'pointer',
  borderStyle: 'solid',
  borderWidth: 'thin',
  borderRadius: 'medium',
  borderColor: 'border.strong',
  transition: 'border-color 0.1s ease-in-out',
  _focusVisible: {
    ...hoverAndFocusStyles,
    outlineWidth: 'thick',
    outlineColor: 'border.focused',
    outlineStyle: 'solid',
    outlineOffset: '1px',
  },
  _hover: hoverAndFocusStyles,
};

const SidebarButtonContent = ({ children, showMenuIcon = true }: SidebarButtonContentProps) => {
  return (
    <HStack
      css={{
        width: '100%',
        alignItems: 'center',
        mx: 'space.150',
        my: 'space.100',
      }}
    >
      <VStack css={{ flexGrow: 1 }}>{children}</VStack>
      {showMenuIcon && <Icon css={{ d: 'block' }} size="small" color="icon.onPrimary" name="Ellipses" />}
    </HStack>
  );
};

const SidebarButtonErrorState = ({ message = 'Something went wrong.' }: { message?: string }) => {
  return (
    <HStack css={{ gap: 'space.100' }}>
      <Icon color="icon.onPrimary" size="small" name="ExclamationCircle" />
      <VStack>
        <Text color="text.onPrimary" variant="text50">
          {message}
          <br />
          <u>Try again</u>
        </Text>
      </VStack>
    </HStack>
  );
};

const SidebarButtonComponent = forwardRef<HTMLButtonElement, SidebarButtonProps>(
  ({ label, children, ...props }, forwardedRef) => {
    return (
      <button type="button" ref={forwardedRef} aria-label={label} className={css(baseUnstyledButtonStyles)} {...props}>
        {children}
      </button>
    );
  }
);

type CompositeComponent = typeof SidebarButtonComponent & {
  Content: typeof SidebarButtonContent;
  ErrorState: typeof SidebarButtonErrorState;
};

const SidebarButton = SidebarButtonComponent as CompositeComponent;

SidebarButtonContent.displayName = 'SidebarButton.Content';

SidebarButton.Content = SidebarButtonContent;
SidebarButton.ErrorState = SidebarButtonErrorState;

export default SidebarButton;
